(() => {
  if (customElements.get('cf-product')) return;

  class CFProduct extends HTMLElement {
    constructor() {
      super();
      this.config = {};
      this.variants = [];
      this.currentVariant = null;
      this.currentMediaIndex = 0;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.scrollTicking = false;
      this.cartDrawerObserver = null;
      this.cartRefreshTimer = null;
      this.cartRefreshRequestId = 0;
      this.cartTotal = 0;
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.config = this.parseJSON('[data-cf-product-config]') || {};
      this.variants = this.parseJSON('[data-cf-product-variants]') || [];
      this.cartTotal = Number(this.config.cartTotal) || 0;
      this.currentVariant = this.variants.find(
        (variant) => String(variant.id) === String(this.dataset.selectedVariantId)
      ) || this.variants[0] || null;

      this.cacheElements();
      this.initGallery();
      this.initZoom();
      this.initVariantPicker();
      this.initVariantGroups();
      this.initQuantity();
      this.initProductForm();
      this.initCartDrawerPresentation();
      this.initCartTracking();
      this.initStickyBar();

      if (this.currentVariant) {
        this.updateProductState(this.currentVariant, false);
        void this.refreshCartState();
      } else {
        this.setUnavailableState();
      }
    }

    disconnectedCallback() {
      if (this.handleStickyScroll) {
        window.removeEventListener('scroll', this.handleStickyScroll);
        window.removeEventListener('resize', this.handleStickyScroll);
      }
      this.cartDrawerObserver?.disconnect();
      window.clearTimeout(this.cartRefreshTimer);
      if (this.handleCartMutation) {
        document.removeEventListener('shopify:cart:lines-update', this.handleCartMutation);
        document.removeEventListener('shopify:cart:discount-update', this.handleCartMutation);
      }
    }

    parseJSON(selector) {
      const element = this.querySelector(selector);
      if (!element) return null;

      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.error('CF product JSON could not be parsed.', error);
        return null;
      }
    }

    cacheElements() {
      this.form = this.querySelector('[data-product-form]');
      this.variantIdInput = this.querySelector('[data-variant-id-input]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.addButtonText = this.querySelector('[data-add-to-cart-text]');
      this.addButtonSpinner = this.querySelector('[data-add-to-cart-spinner]');
      this.feedback = this.querySelector('[data-form-feedback]');
      this.variantMessage = this.querySelector('[data-variant-message]');
      this.priceCurrent = this.querySelector('[data-price-current]');
      this.priceCompare = this.querySelector('[data-price-compare]');
      this.saleBadge = this.querySelector('[data-sale-badge]');
      this.unitPrice = this.querySelector('[data-unit-price]');
      this.sku = this.querySelector('[data-product-sku]');
      this.availability = this.querySelector('[data-availability]');
      this.availabilityText = this.querySelector('[data-availability-text]');
      this.freeShipping = this.querySelector('[data-free-shipping]');
      this.freeShippingMessage = this.querySelector('[data-free-shipping-message]');
      this.freeShippingProgressBar = this.querySelector('[data-free-shipping-progressbar]');
      this.freeShippingProgress = this.querySelector('[data-free-shipping-progress]');
      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.purchaseArea = this.querySelector('[data-purchase-area]');
      this.stickyBar = this.querySelector('[data-sticky-bar]');
      this.stickyButton = this.querySelector('[data-sticky-submit]');
      this.stickyPrice = this.querySelector('[data-sticky-price]');
      this.stickyTitle = this.querySelector('[data-sticky-title]');
    }

    initGallery() {
      this.stage = this.querySelector('[data-gallery-stage]');
      this.slides = Array.from(this.querySelectorAll('[data-media-slide]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-media-thumbnail]'));
      this.previousButton = this.querySelector('[data-gallery-previous]');
      this.nextButton = this.querySelector('[data-gallery-next]');
      this.galleryStatus = this.querySelector('[data-gallery-status]');

      if (!this.stage || this.slides.length === 0) return;

      const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.currentMediaIndex = activeIndex >= 0 ? activeIndex : 0;

      this.thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => this.selectMedia(index, true));
      });

      this.previousButton?.addEventListener('click', () => this.selectMedia(this.currentMediaIndex - 1, true));
      this.nextButton?.addEventListener('click', () => this.selectMedia(this.currentMediaIndex + 1, true));

      this.stage.addEventListener('touchstart', (event) => {
        if (event.target.closest('video, iframe, model-viewer')) return;
        const touch = event.changedTouches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      }, { passive: true });

      this.stage.addEventListener('touchend', (event) => {
        if (event.target.closest('video, iframe, model-viewer')) return;
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;

        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        this.selectMedia(deltaX < 0 ? this.currentMediaIndex + 1 : this.currentMediaIndex - 1, false);
      }, { passive: true });

      this.selectMedia(this.currentMediaIndex, false);
    }

    selectMedia(index, focusThumbnail = false) {
      if (!this.slides?.length) return;

      const total = this.slides.length;
      const normalizedIndex = ((index % total) + total) % total;
      this.currentMediaIndex = normalizedIndex;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === normalizedIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');

        if (!active) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
        }
      });

      this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const active = thumbnailIndex === normalizedIndex;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
        thumbnail.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      const activeSlide = this.slides[normalizedIndex];
      const aspect = Number.parseFloat(activeSlide.dataset.mediaAspect || '1');
      this.stage.style.setProperty('--cf-product-active-media-ratio', aspect > 0 ? aspect : 1);

      if (this.galleryStatus) {
        this.galleryStatus.textContent = `${normalizedIndex + 1} / ${total}`;
      }

      const activeThumbnail = this.thumbnails[normalizedIndex];
      if (activeThumbnail) {
        activeThumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        if (focusThumbnail) activeThumbnail.focus({ preventScroll: true });
      }
    }

    selectMediaById(mediaId) {
      if (!mediaId || !this.slides?.length) return;
      const index = this.slides.findIndex(
        (slide) => String(slide.dataset.mediaId) === String(mediaId)
      );
      if (index >= 0) this.selectMedia(index, false);
    }

    initZoom() {
      this.zoomDialog = this.querySelector('[data-zoom-dialog]');
      this.zoomImage = this.querySelector('[data-zoom-image]');
      this.zoomClose = this.querySelector('[data-zoom-close]');

      if (!this.zoomDialog || !this.zoomImage) return;

      this.querySelectorAll('[data-cf-product-zoom-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          this.zoomImage.src = trigger.dataset.zoomSrc || '';
          this.zoomImage.alt = trigger.dataset.zoomAlt || '';

          if (typeof this.zoomDialog.showModal === 'function') {
            this.zoomDialog.showModal();
          } else {
            this.zoomDialog.setAttribute('open', '');
          }
        });
      });

      this.zoomClose?.addEventListener('click', () => this.closeZoom());
      this.zoomDialog.addEventListener('click', (event) => {
        if (event.target === this.zoomDialog) this.closeZoom();
      });
    }

    closeZoom() {
      if (!this.zoomDialog) return;
      if (typeof this.zoomDialog.close === 'function') {
        this.zoomDialog.close();
      } else {
        this.zoomDialog.removeAttribute('open');
      }
    }

    initVariantPicker() {
      this.optionGroups = Array.from(this.querySelectorAll('[data-option-group]'));
      if (this.optionGroups.length === 0) return;

      this.optionGroups.forEach((group) => {
        group.querySelectorAll('[data-option-control]').forEach((control) => {
          control.addEventListener('change', () => this.handleOptionChange());
        });
      });

      this.updateSelectedOptionLabels();
      this.updateOptionAvailability();
    }

    initVariantGroups() {
      this.querySelectorAll('[data-cf-variant-group-root]').forEach((root) => {
        const items = Array.from(root.querySelectorAll('[data-cf-variant-group-item]'));
        if (items.length === 0) return;

        const openItem = (item) => {
          items.forEach((candidate) => {
            const isOpen = candidate === item;
            const header = candidate.querySelector('[data-cf-variant-group-header]');
            const content = candidate.querySelector('[data-cf-variant-group-content]');
            candidate.classList.toggle('is-open', isOpen);
            header?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            if (content) content.hidden = !isOpen;
          });
        };

        items.forEach((item) => {
          const header = item.querySelector('[data-cf-variant-group-header]');
          header?.addEventListener('click', () => openItem(item));

          item.querySelectorAll('[data-option-control]').forEach((control) => {
            control.addEventListener('change', () => {
              if (control.checked) openItem(item);
            });
          });
        });

        const serverOpenItem = items.find((item) => item.classList.contains('is-open'));
        const selectedItem = items.find((item) => item.querySelector('input[data-option-control]:checked'));
        openItem(serverOpenItem || selectedItem || items[0]);
      });
    }

    handleOptionChange() {
      this.updateSelectedOptionLabels();
      this.updateOptionAvailability();

      const selectedOptions = this.getSelectedOptions();
      const variant = this.findVariant(selectedOptions);

      if (variant) {
        this.currentVariant = variant;
        this.updateProductState(variant, true);
      } else {
        this.currentVariant = null;
        this.setUnavailableState();
      }
    }

    getSelectedOptions() {
      return this.optionGroups.map((group) => {
        const select = group.querySelector('select[data-option-control]');
        if (select) return select.value;

        const checked = group.querySelector('input[data-option-control]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(selectedOptions) {
      if (selectedOptions.some((value) => value === null || value === '')) return null;
      return this.variants.find((variant) => variant.options.every(
        (value, index) => String(value) === String(selectedOptions[index])
      )) || null;
    }

    updateSelectedOptionLabels() {
      this.optionGroups.forEach((group) => {
        const output = group.querySelector('[data-selected-option-value]');
        if (!output) return;

        const select = group.querySelector('select[data-option-control]');
        const checked = group.querySelector('input[data-option-control]:checked');
        output.textContent = select ? select.value : checked?.value || '';
      });
    }

    updateOptionAvailability() {
      const selectedOptions = this.getSelectedOptions();

      const isValueAvailable = (position, candidateValue) => this.variants.some((variant) => {
        if (!variant.available) return false;
        if (String(variant.options[position]) !== String(candidateValue)) return false;

        return selectedOptions.every((selectedValue, selectedPosition) => {
          if (selectedPosition === position || selectedValue === null || selectedValue === '') return true;
          return String(variant.options[selectedPosition]) === String(selectedValue);
        });
      });

      this.optionGroups.forEach((group, position) => {
        const select = group.querySelector('select[data-option-control]');

        if (select) {
          Array.from(select.options).forEach((option) => {
            const available = isValueAvailable(position, option.value);
            option.disabled = !available && !option.selected;
          });
          return;
        }

        group.querySelectorAll('input[data-option-control]').forEach((control) => {
          const available = isValueAvailable(position, control.value);
          control.disabled = !available && !control.checked;

          const label = control.nextElementSibling;
          if (label) {
            label.classList.toggle('is-unavailable', !available);
            label.setAttribute('aria-disabled', available ? 'false' : 'true');
          }
        });
      });
    }

    updateProductState(variant, updateUrl = true) {
      if (!variant) return;

      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateQuantityRules(variant);
      this.updateFreeShipping();
      this.updateSku(variant);
      this.selectMediaById(variant.featured_media_id);

      if (this.variantMessage) {
        this.variantMessage.hidden = true;
        this.variantMessage.textContent = '';
      }

      if (this.addButton) {
        this.addButton.disabled = !variant.available;
        this.setAddButtonLabel(variant.available ? this.config.addToCartLabel : this.config.soldOutLabel);
      }

      if (this.stickyButton) {
        this.stickyButton.disabled = !variant.available;
        this.stickyButton.textContent = variant.available ? this.config.addToCartLabel : this.config.soldOutLabel;
      }

      if (updateUrl && this.config.updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    setUnavailableState() {
      if (this.variantIdInput) this.variantIdInput.value = '';
      if (this.variantMessage) {
        this.variantMessage.hidden = false;
        this.variantMessage.textContent = this.config.unavailableMessage;
      }
      if (this.addButton) {
        this.addButton.disabled = true;
        this.setAddButtonLabel(this.config.unavailableLabel);
      }
      if (this.stickyButton) {
        this.stickyButton.disabled = true;
        this.stickyButton.textContent = this.config.unavailableLabel;
      }
      if (this.availabilityText) this.availabilityText.textContent = this.config.unavailableLabel;
      this.availability?.classList.remove('is-available');
      if (this.freeShipping) this.freeShipping.hidden = true;
    }

    updatePrice(variant) {
      if (this.priceCurrent) this.priceCurrent.textContent = this.formatMoney(variant.price);
      if (this.stickyPrice) this.stickyPrice.textContent = this.formatMoney(variant.price);

      const onSale = Number(variant.compare_at_price) > Number(variant.price);

      if (this.priceCompare) {
        this.priceCompare.hidden = !onSale;
        this.priceCompare.textContent = onSale ? this.formatMoney(variant.compare_at_price) : '';
      }

      if (this.saleBadge) {
        this.saleBadge.hidden = !onSale;
        if (onSale) {
          if (this.config.saleBadgeStyle === 'percentage') {
            const percentage = Math.round(
              ((Number(variant.compare_at_price) - Number(variant.price)) / Number(variant.compare_at_price)) * 100
            );
            this.saleBadge.textContent = `${percentage}% ${this.config.salePercentageSuffix}`.trim();
          } else {
            this.saleBadge.textContent = this.config.saleLabel;
          }
        }
      }

      if (this.unitPrice) {
        const hasUnitPrice = Number(variant.unit_price) > 0 && variant.unit_price_measurement;
        this.unitPrice.hidden = !hasUnitPrice;
        this.unitPrice.textContent = hasUnitPrice ? this.formatUnitPrice(variant) : '';
      }
    }

    updateAvailability(variant) {
      if (!this.availability || !this.availabilityText) return;
      this.availability.classList.toggle('is-available', Boolean(variant.available));
      this.availabilityText.textContent = variant.available
        ? this.config.availableLabel
        : this.config.soldOutLabel;
    }

    updateSku(variant) {
      if (!this.sku) return;
      const hasSku = Boolean(variant.sku);
      this.sku.hidden = !hasSku;
      this.sku.textContent = hasSku ? `${this.config.skuLabel}: ${variant.sku}` : '';
    }

    updateQuantityRules(variant) {
      if (!this.quantityInput) return;
      const rule = variant.quantity_rule || {};
      const minimum = Number(rule.min) || 1;
      const increment = Number(rule.increment) || 1;
      const maximum = Number(rule.max) || null;

      this.quantityInput.min = minimum;
      this.quantityInput.step = increment;
      if (maximum) {
        this.quantityInput.max = maximum;
      } else {
        this.quantityInput.removeAttribute('max');
      }

      const current = Number(this.quantityInput.value) || minimum;
      this.quantityInput.value = Math.max(minimum, maximum ? Math.min(current, maximum) : current);
    }

    initQuantity() {
      if (!this.quantityInput) return;

      this.querySelectorAll('[data-quantity-action]').forEach((button) => {
        button.addEventListener('click', () => {
          const action = button.dataset.quantityAction;
          const current = Number(this.quantityInput.value) || Number(this.quantityInput.min) || 1;
          const step = Number(this.quantityInput.step) || 1;
          const minimum = Number(this.quantityInput.min) || 1;
          const maximum = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
          const next = action === 'increase' ? current + step : current - step;

          this.quantityInput.value = Math.min(maximum, Math.max(minimum, next));
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      this.quantityInput.addEventListener('change', () => {
        const minimum = Number(this.quantityInput.min) || 1;
        const maximum = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
        const value = Number(this.quantityInput.value) || minimum;
        this.quantityInput.value = Math.min(maximum, Math.max(minimum, value));
        this.updateFreeShipping();
      });
    }

    updateFreeShipping() {
      if (!this.freeShipping || !this.freeShippingMessage) return;

      const threshold = Number(this.config.freeShippingThreshold) || 0;
      const cartTotal = Math.max(0, Number(this.cartTotal) || 0);
      const remaining = Math.max(0, threshold - cartTotal);
      const progress = threshold > 0
        ? Math.min(100, Math.max(0, (cartTotal / threshold) * 100))
        : 0;

      if (threshold <= 0) {
        this.freeShipping.hidden = true;
        return;
      }

      this.freeShipping.hidden = false;
      this.freeShipping.classList.toggle('is-qualified', remaining === 0);

      if (remaining > 0) {
        const template = this.config.freeShippingProgressText
          || 'Add [amount] more to qualify for free shipping.';
        this.freeShippingMessage.textContent = template
          .split('[amount]')
          .join(this.formatMoney(remaining));
      } else {
        this.freeShippingMessage.textContent = this.config.freeShippingQualifiedText
          || 'You qualify for free shipping.';
      }

      if (this.freeShippingProgress) {
        this.freeShippingProgress.style.width = `${progress}%`;
      }

      if (this.freeShippingProgressBar) {
        this.freeShippingProgressBar.setAttribute('aria-valuenow', String(Math.round(progress)));
        this.freeShippingProgressBar.setAttribute('aria-valuetext', this.freeShippingMessage.textContent);
      }
    }

    commitSelectionToCartTotal() {
      if (!this.currentVariant) return;

      const quantity = Math.max(1, Number(this.quantityInput?.value) || 1);
      this.cartTotal += (Number(this.currentVariant.price) || 0) * quantity;
      this.updateFreeShipping();
    }

    initProductForm() {
      if (!this.form || !this.addButton) return;

      this.form.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (!this.currentVariant) {
          this.showFeedback(this.config.unavailableMessage, true);
          return;
        }

        if (!this.currentVariant.available) {
          this.showFeedback(this.config.soldOutLabel, true);
          return;
        }

        this.setLoading(true);
        this.clearFeedback();

        try {
          const cartSurface = this.config.afterAddBehavior === 'drawer'
            ? document.querySelector('cart-drawer') || document.querySelector('cart-notification')
            : null;
          const canRenderLegacyCart = Boolean(
            cartSurface
            && typeof cartSurface.getSectionsToRender === 'function'
            && typeof cartSurface.renderContents === 'function'
          );

          if (
            this.config.afterAddBehavior === 'drawer'
            && !canRenderLegacyCart
            && window.Shopify?.actions?.updateCart
            && window.Shopify?.actions?.openCart
          ) {
            const quantity = Number(this.quantityInput?.value) || 1;
            const nativeResult = await window.Shopify.actions.updateCart(
              {
                lines: [{
                  merchandiseId: `gid://shopify/ProductVariant/${this.currentVariant.id}`,
                  quantity
                }]
              },
              { event: { context: 'product' } }
            );

            if (nativeResult?.userErrors?.length) {
              throw new Error(nativeResult.userErrors[0].message || this.config.genericError);
            }

            this.commitSelectionToCartTotal();
            await this.refreshCartState();
            await window.Shopify.actions.openCart();
            this.showAddedState();
            return;
          }

          const formData = new FormData(this.form);
          this.form.querySelectorAll('[data-option-control][name]').forEach((control) => {
            formData.delete(control.name);
          });

          if (cartSurface) {
            const sections = typeof cartSurface.getSectionsToRender === 'function'
              ? cartSurface.getSectionsToRender().map((section) => section.id)
              : ['cart-drawer', 'cart-icon-bubble'];

            formData.append('sections', sections.join(','));
            formData.append('sections_url', window.location.pathname);
            if (typeof cartSurface.setActiveElement === 'function') {
              cartSurface.setActiveElement(document.activeElement);
            }
          }

          const response = await fetch(this.config.cartAddUrl, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });

          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.description || result.message || this.config.genericError);
          }

          this.commitSelectionToCartTotal();
          await this.refreshCartState();

          if (this.config.afterAddBehavior === 'cart') {
            window.location.href = this.config.cartUrl;
            return;
          }

          if (this.config.afterAddBehavior === 'drawer' && this.openCartSurface(cartSurface, result)) {
            this.showAddedState();
            return;
          }

          this.showSuccessFeedback();
          this.showAddedState();
        } catch (error) {
          this.showFeedback(error.message || this.config.genericError, true);
          this.setAddButtonLabel(
            this.currentVariant?.available ? this.config.addToCartLabel : this.config.soldOutLabel
          );
        } finally {
          this.setLoading(false);
        }
      });
    }

    initCartDrawerPresentation() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;

      const updatePresentation = () => this.updateCartDrawerPresentation(cartDrawer);
      updatePresentation();

      if (typeof MutationObserver !== 'function') return;
      this.cartDrawerObserver = new MutationObserver(() => {
        updatePresentation();
        this.scheduleCartRefresh();
      });
      this.cartDrawerObserver.observe(cartDrawer, { childList: true, subtree: true });
    }

    initCartTracking() {
      if (!this.freeShipping) return;

      this.handleCartMutation = (event) => {
        if (event.promise && typeof event.promise.then === 'function') {
          event.promise
            .then(() => this.scheduleCartRefresh(0))
            .catch(() => {});
          return;
        }

        this.scheduleCartRefresh();
      };

      document.addEventListener('shopify:cart:lines-update', this.handleCartMutation);
      document.addEventListener('shopify:cart:discount-update', this.handleCartMutation);
    }

    updateCartDrawerPresentation(cartSurface) {
      if (!cartSurface) return;

      const translatedLabel = String(this.config.cartSubtotalLabel || '').trim();
      const subtotalLabel = translatedLabel
        && !translatedLabel.toLowerCase().includes('translation missing')
        ? translatedLabel
        : 'Subtotal';

      cartSurface.querySelectorAll('.totals__total').forEach((heading) => {
        if (heading.textContent.trim() !== subtotalLabel) {
          heading.textContent = subtotalLabel;
        }
      });

    }

    showAddedState() {
      this.setAddButtonLabel(this.config.addedLabel);
      window.setTimeout(() => {
        if (this.currentVariant?.available) this.setAddButtonLabel(this.config.addToCartLabel);
      }, 1800);
    }

    openCartSurface(cartSurface, result) {
      if (!cartSurface) return false;

      try {
        if (typeof cartSurface.renderContents === 'function' && result.sections) {
          const requestedSections = typeof cartSurface.getSectionsToRender === 'function'
            ? cartSurface.getSectionsToRender().map((section) => section.id)
            : Object.keys(result.sections);
          const hasValidSections = requestedSections.length > 0
            && requestedSections.every((id) => typeof result.sections[id] === 'string');

          if (!hasValidSections) return false;

          /*
           * Dawn-style drawers keep their outer custom element when the returned
           * section HTML is rendered. If the cart was empty on page load, that
           * element (and, in some theme versions, cart-drawer-items) can retain
           * `is-empty`. The drawer then becomes active but only its overlay is
           * visible. Clear the stale state both before and after the native
           * renderer swaps its contents.
           */
          const clearStaleEmptyState = () => {
            cartSurface.classList.remove('is-empty');
            cartSurface
              .querySelectorAll('.drawer__inner.is-empty, cart-drawer-items.is-empty')
              .forEach((element) => element.classList.remove('is-empty'));
            this.updateCartDrawerPresentation(cartSurface);
          };

          clearStaleEmptyState();
          cartSurface.renderContents(result);
          clearStaleEmptyState();

          // Some themes finish updating their drawer contents asynchronously, so
          // clear the stale empty state once more after their renderer settles.
          // The native renderContents() method owns the open/close lifecycle. Do
          // not call open() here: a delayed fallback can reactivate a drawer that
          // the customer has already closed, leaving its backdrop over the page.
          window.setTimeout(() => {
            clearStaleEmptyState();
          }, 50);
          return true;
        }
      } catch (error) {
        return false;
      }

      return false;
    }

    scheduleCartRefresh(delay = 180) {
      window.clearTimeout(this.cartRefreshTimer);
      this.cartRefreshTimer = window.setTimeout(() => {
        void this.refreshCartState();
      }, delay);
    }

    async refreshCartState() {
      if (!this.config.cartJsonUrl) return;

      const requestId = ++this.cartRefreshRequestId;

      try {
        const response = await fetch(this.config.cartJsonUrl, {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        if (!response.ok) return;
        const cart = await response.json();
        if (requestId !== this.cartRefreshRequestId || !this.isConnected) return;

        this.cartTotal = Number(cart.total_price ?? cart.items_subtotal_price) || 0;
        this.updateFreeShipping();

        document.querySelectorAll('[data-cart-count]').forEach((element) => {
          element.textContent = cart.item_count;
        });

        document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
          bubble.classList.toggle('hidden', cart.item_count === 0);
          bubble.querySelectorAll('span').forEach((span) => {
            if (!span.classList.contains('visually-hidden')) span.textContent = cart.item_count;
          });
        });
      } catch (error) {
        // Keep the last known cart total and product controls usable if refresh fails.
      }
    }

    setLoading(loading) {
      if (!this.addButton) return;
      this.addButton.classList.toggle('is-loading', loading);
      this.addButton.setAttribute('aria-busy', loading ? 'true' : 'false');
      this.addButton.disabled = loading || !this.currentVariant?.available;
      if (this.addButtonSpinner) this.addButtonSpinner.hidden = !loading;
      if (loading) this.setAddButtonLabel(this.config.addingLabel);
      if (this.stickyButton) this.stickyButton.disabled = loading || !this.currentVariant?.available;
    }

    setAddButtonLabel(label) {
      if (this.addButtonText) this.addButtonText.textContent = label || '';
    }

    clearFeedback() {
      if (!this.feedback) return;
      this.feedback.hidden = true;
      this.feedback.textContent = '';
      this.feedback.classList.remove('cf-product__form-feedback--error');
    }

    showFeedback(message, isError = false) {
      if (!this.feedback) return;
      this.feedback.hidden = false;
      this.feedback.textContent = message;
      this.feedback.classList.toggle('cf-product__form-feedback--error', isError);
      this.feedback.focus({ preventScroll: true });
    }

    showSuccessFeedback() {
      if (!this.feedback) return;
      this.feedback.hidden = false;
      this.feedback.textContent = '';
      this.feedback.classList.remove('cf-product__form-feedback--error');
      this.feedback.append(document.createTextNode(`${this.config.successMessage} `));

      const link = document.createElement('a');
      link.href = this.config.cartUrl;
      link.textContent = this.config.viewCartLabel;
      this.feedback.append(link);
      this.feedback.focus({ preventScroll: true });
    }

    initStickyBar() {
      if (!this.stickyBar || !this.purchaseArea || !this.config.stickyMobile) return;

      this.stickyButton?.addEventListener('click', () => {
        if (this.form && this.addButton && !this.addButton.disabled) {
          this.form.requestSubmit(this.addButton);
        }
      });

      this.handleStickyScroll = () => {
        if (this.scrollTicking) return;
        this.scrollTicking = true;

        window.requestAnimationFrame(() => {
          const mobile = window.matchMedia('(max-width: 989px)').matches;
          const purchaseRect = this.purchaseArea.getBoundingClientRect();
          const shouldShow = mobile && purchaseRect.bottom < 0;
          this.stickyBar.classList.toggle('is-visible', shouldShow);
          this.stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
          this.scrollTicking = false;
        });
      };

      window.addEventListener('scroll', this.handleStickyScroll, { passive: true });
      window.addEventListener('resize', this.handleStickyScroll, { passive: true });
      this.handleStickyScroll();
    }

    formatUnitPrice(variant) {
      const measurement = variant.unit_price_measurement;
      if (!measurement) return '';
      const referenceValue = Number(measurement.reference_value) === 1
        ? ''
        : `${measurement.reference_value} `;
      return `${this.formatMoney(variant.unit_price)} / ${referenceValue}${measurement.reference_unit}`;
    }

    formatMoney(cents) {
      const format = this.config.moneyFormat || '${{amount}}';
      const value = Number(cents) || 0;
      const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
      const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';

      const formatNumber = (amount, decimals = 2, thousands = ',', decimal = '.') => {
        const number = (amount / 100).toFixed(decimals);
        const parts = number.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.join(decimal);
      };

      let formatted;
      switch (placeholder) {
        case 'amount_no_decimals':
          formatted = formatNumber(value, 0);
          break;
        case 'amount_with_comma_separator':
          formatted = formatNumber(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          formatted = formatNumber(value, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          formatted = formatNumber(value, 2, "'", '.');
          break;
        default:
          formatted = formatNumber(value, 2);
      }

      return placeholderMatch ? format.replace(placeholderMatch[0], formatted) : formatted;
    }
  }

  customElements.define('cf-product', CFProduct);
})();
